import React, { useLayoutEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import Footer from "../components/layout/Footer";
import {
  CssBaseline,
  Paper as MuiPaper,
  Theme,
  useTheme,
  useMediaQuery,
  Backdrop,
  IconButton,
  Box,
} from "@mui/material";
import AppContext from "../context/AppContext";
import _ from "lodash";
import { withSidebarLayoutRoutes as routes } from "../routes/index";
import Loader from "../components/Loader";
import styled from "@emotion/styled/macro";
import TopBar from "../components/layout/TopBar";
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";
import { hasFeatureSubscription } from "../utils/user";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { IS_APP_QUERY, SIGN_IN_PATH } from "../constants/routes";
import GoToTop from "../components/GoToTop";
import { Helmet } from "react-helmet";
import { convertToRGA } from "../utils/utils";
import { CookiePreferencesModel } from "../models/Cookie";
import { PERSONAL_COOKIE } from "../constants/cookies";

const Root = styled.div`
  display: flex;
  min-height: 100vh;
  min-width: 320px;
  * {
    font-family: ${({ theme }: { theme: Theme }) => theme.typography.fontFamily};
  }
  @media print {
    .MuiFab-root {
      display: none;
    }
  }
`;5

const Drawer = styled(Box)`
  @media print {
    display: none;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(to bottom,
    ${({ theme }: { theme: Theme }) => convertToRGA(theme.palette.background.paper, 0.0)},
    ${({ theme }: { theme: Theme }) => convertToRGA(theme.palette.background.paper, 0.7)} 150px);
  transition: background 0.6s linear;
`;

const MainContent = styled(MuiPaper)`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: transparent;
  position: relative;
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }
  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
  @media print {
    &:before {
      opacity: 0.2;
      content: " ";
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 100%;
      z-index: 0;
      background-image: url("data:image/png;base64,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");
      background-position: 0px 0px;
      background-size: 794px 1123px;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`;

const WithSidebarLayout = ({ children }: { children: any }) => {
  const appContext = React.useContext(AppContext);
  const { user } = appContext;
  const history = useHistory();
  const [mobileOpen, setMobileOpen] = useState(false);

  const theme = useTheme();
  const isMobileDrawer = useMediaQuery(theme.breakpoints.down('md'));
  const isXLSize = useMediaQuery(theme.breakpoints.down("xl"));
  const pCookie = localStorage.getItem(PERSONAL_COOKIE);
  const cookiesPref: CookiePreferencesModel = pCookie !== null ? JSON.parse(pCookie) : {
    analytics: {
      googleAnalytics: true,
      hotjar: true
    },
    functional: {
      analsesFn: true,
      rememberMe: true
    }
  };

  const location = useLocation();
  const isAppWebview = new URLSearchParams(location.search).get(IS_APP_QUERY) !== null;
  const drawerWidth = isXLSize ? 220 : 250;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useLayoutEffect(() => {
    if (!isMobileDrawer)
      setMobileOpen(false);

  }, [isMobileDrawer]);

  const { t }: { t: any } = useTranslation();

  const filteredRoutes = _.filter(routes, (r: any) => {
    return user && hasFeatureSubscription(user, r.featureCodes);

  });

  if (user === null) {
    if (appContext.loading) {
      return <Loader />;
    } else {
      return <Redirect to={SIGN_IN_PATH} />
    }
  }

  const handleBackClick = () => {
    if (history.action !== 'POP') {
      history.goBack();
    }
  };

  window.addEventListener("popstate", handleBackClick);

  return (
    <Root theme={theme}>
      {
        cookiesPref.analytics.hotjar &&
        (process.env.NODE_ENV !== "development") &&
        (
          <Helmet>
            <script>
              {
                `(function (h, o, t, j, a, r) {
                  h.hj =
                    h.hj ||
                    function () {
                      (h.hj.q = h.hj.q || []).push(arguments);
                    };
                  h._hjSettings = { hjid: 2947693, hjsv: 6 };
                  a = o.getElementsByTagName("head")[0];
                  r = o.createElement("script");
                  r.async = 1;
                  r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
                  a.appendChild(r);
                })(
                  window,
                  document,
                  "https://static.hotjar.com/c/hotjar-",
                  ".js?sv="
                );`
              }
            </script>
          </Helmet>
        )
      }
      <CssBaseline />
      {
        !isAppWebview && (
          <Drawer
            sx={{
              [theme.breakpoints.up("md")]: {
                width: drawerWidth,
                flexShrink: 0
              }
            }}
          >
            {isMobileDrawer ?
              <Sidebar
                routes={filteredRoutes}
                PaperProps={{ style: { width: drawerWidth } }}
                variant="persistent"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                  keepMounted: true,
                }}
              /> :
              <Sidebar
                routes={filteredRoutes}
                PaperProps={{ style: { width: drawerWidth } }}
              />
            }
          </Drawer>
        )
      }
      <AppContent theme={theme}>
        <TopBar
          appContext={appContext}
          onDrawerToggle={handleDrawerToggle}
          isDrawerOpen={mobileOpen}
        />
        <MainContent
          theme={theme}
          sx={{
            paddingLeft: isXLSize ? "25px" : theme.spacing(10),
            paddingRight: isXLSize ? "25px" : theme.spacing(10),
          }}
        >
          {children}
        </MainContent>
        <Footer />
        <GoToTop />
        <Backdrop
          open={mobileOpen}
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer - 1 }}>
          <IconButton
            aria-label={t("Close Menu")}
            color="inherit"
            onClick={handleDrawerToggle}
            sx={{
              position: "absolute",
              top: theme.spacing(3),
              left: drawerWidth
            }}>
            <CloseIcon color="inherit" />
          </IconButton>
        </Backdrop>
      </AppContent>
    </Root>
  );
};

export default WithSidebarLayout;
